import React from "react";
import {
	BookingContentName,
	BookingContentWrap,
	BookingDashboardCard,
	BookingDashboardEyebrow,
	BookingDashboardLabel,
	BookingDashboardList,
	BookingDashboardListItem,
	BookingDashboardMeta,
	BookingDashboardMetaStrong,
	BookingDashboardPrimaryCard,
	BookingDashboardPrimaryFooter,
	BookingDashboardPrimaryValue,
	BookingDashboardPrimaryValueSub,
	BookingDashboardSecondaryCard,
	BookingDashboardTrend,
	BookingDashboardValue,
	BookingDashboardWrap,
	PaginationWrapper,
	PaginationWrapperTop,
} from "../styled/DesStyled";
import BookingTable from "./BookingTable";
import useLoadedUser from "../../../hooks/auth/useLoadedUser";
import useCommonListFunctions from "../../../hooks/useCommonList/useCommonListFunctions";
import NumberPaginationBox from "../../../controls/components/numberPaginationBox/NumberPaginationBox";
import { PERPAGE } from "../../Partner/PartnerManagement";
import { UserRole } from "../../../../models/User";
import BookingTableAdmin from "./Admin/BookingTableAdmin";
import BookingTablePartner from "./Partner/BookingTablePartner";
import { PriceDetail } from "../../../../models/Order";
import { AdminOrderRevenueSummary } from "../../../../services/OrderService";

function formatCompactAmount(value: number, currencyCode: string) {
	return `${value.toLocaleString("en-US", {
		minimumFractionDigits: 2,
		maximumFractionDigits: 2,
	})} ${currencyCode}`;
}

function getPrimaryRevenueLabel(summary: AdminOrderRevenueSummary | null, loading?: boolean) {
	if (loading) {
		return {
			title: "Loading revenue...",
			subtitle: "Calculating station commission from the active booking filters",
		};
	}

	if (!summary || !summary.currencyBreakdown?.length) {
		return {
			title: "No revenue in current filter",
			subtitle: "Change country, city or time range to inspect commission totals",
		};
	}

	if (summary.totalCommission !== null && summary.currencyCode) {
		return {
			title: formatCompactAmount(summary.totalCommission, summary.currencyCode),
			subtitle: "Total station commission from the current filtered booking set",
		};
	}

	return {
		title: `${summary.currencyBreakdown.length} active currencies`,
		subtitle: "Revenue is split by station currency, shown below by strongest totals",
	};
}

function getTopCurrencyBreakdown(summary: AdminOrderRevenueSummary | null) {
	if (!summary?.currencyBreakdown?.length) return [];

	return [...summary.currencyBreakdown]
		.sort((a, b) => b.totalCommission - a.totalCommission)
		.slice(0, 6);
}

function getRevenueMeta(summary: AdminOrderRevenueSummary | null) {
	if (!summary?.currencyBreakdown?.length) return null;

	const strongestCurrency = [...summary.currencyBreakdown].sort(
		(a, b) => b.totalCommission - a.totalCommission
	)[0];

	if (!strongestCurrency) return null;

	return `Top market: ${formatCompactAmount(strongestCurrency.totalCommission, strongestCurrency.currencyCode)}`;
}

function getBookingPerBag(summary: AdminOrderRevenueSummary | null) {
	if (!summary?.bookingCount || !summary?.totalQuantity) return null;
	return `${(summary.totalQuantity / summary.bookingCount).toFixed(2)} bags / booking`;
}

export default function BookingContent(props: {
	listOrder: PriceDetail[];
	reload: () => void;
	count: number;
	revenueSummary?: AdminOrderRevenueSummary | null;
	revenueSummaryLoading?: boolean;
}) {
	const { user } = useLoadedUser();
	const { page, doChangePage } = useCommonListFunctions();
	const primaryRevenue = getPrimaryRevenueLabel(props.revenueSummary || null, props.revenueSummaryLoading);
	const topCurrencyBreakdown = getTopCurrencyBreakdown(props.revenueSummary || null);
	const extraCurrencyCount = Math.max(
		0,
		(props.revenueSummary?.currencyBreakdown?.length || 0) - topCurrencyBreakdown.length
	);
	const bookingPerBag = getBookingPerBag(props.revenueSummary || null);
	const revenueMeta = getRevenueMeta(props.revenueSummary || null);

	return (
		<>
			<PaginationWrapperTop>
				<NumberPaginationBox
					page={page || 1}
					count={props.count}
					per={PERPAGE.PerPage}
					onChange={(val) => doChangePage?.(val)}
				/>
			</PaginationWrapperTop>
			<BookingContentWrap>
				<BookingContentName>Bookings</BookingContentName>
				{user?.role === UserRole.Admin && (
					<BookingDashboardWrap>
						<BookingDashboardPrimaryCard>
							<BookingDashboardEyebrow
								style={{
									color: "rgba(255,255,255,0.84)",
									background: "rgba(255,255,255,0.12)",
								}}>
								Revenue overview
							</BookingDashboardEyebrow>
							<BookingDashboardLabel style={{ color: "rgba(255,255,255,0.72)" }}>
								Station commission
							</BookingDashboardLabel>
							<BookingDashboardPrimaryValue>{primaryRevenue.title}</BookingDashboardPrimaryValue>
							<BookingDashboardPrimaryValueSub>{primaryRevenue.subtitle}</BookingDashboardPrimaryValueSub>
							<BookingDashboardPrimaryFooter>
								<BookingDashboardMeta>
									<BookingDashboardMetaStrong>{props.revenueSummary?.bookingCount || 0}</BookingDashboardMetaStrong> qualifying
									bookings
								</BookingDashboardMeta>
								<BookingDashboardMeta>
									<BookingDashboardMetaStrong>{props.revenueSummary?.totalQuantity || 0}</BookingDashboardMetaStrong> total bags
								</BookingDashboardMeta>
								{revenueMeta && <BookingDashboardMeta>{revenueMeta}</BookingDashboardMeta>}
							</BookingDashboardPrimaryFooter>
							{topCurrencyBreakdown.length > 0 && (
								<BookingDashboardList>
									{topCurrencyBreakdown.map((item) => (
										<BookingDashboardListItem key={item.currencyCode}>
											<span>{item.currencyCode}</span>
											<strong>{formatCompactAmount(item.totalCommission, item.currencyCode)}</strong>
										</BookingDashboardListItem>
									))}
									{extraCurrencyCount > 0 && (
										<BookingDashboardListItem>
											<span>More markets</span>
											<strong>+{extraCurrencyCount} currencies</strong>
										</BookingDashboardListItem>
									)}
								</BookingDashboardList>
							)}
						</BookingDashboardPrimaryCard>
						<BookingDashboardSecondaryCard>
							<BookingDashboardEyebrow>Volume</BookingDashboardEyebrow>
							<BookingDashboardLabel>Matched bookings</BookingDashboardLabel>
							<BookingDashboardValue>{props.revenueSummary?.bookingCount || 0}</BookingDashboardValue>
							<BookingDashboardTrend>
								Bookings after country, city and date filtering
							</BookingDashboardTrend>
						</BookingDashboardSecondaryCard>
						<BookingDashboardSecondaryCard>
							<BookingDashboardEyebrow>Operations</BookingDashboardEyebrow>
							<BookingDashboardLabel>Total bags</BookingDashboardLabel>
							<BookingDashboardValue>{props.revenueSummary?.totalQuantity || 0}</BookingDashboardValue>
							<BookingDashboardTrend>
								{bookingPerBag || "Used in commission formula with station rate and booking days"}
							</BookingDashboardTrend>
						</BookingDashboardSecondaryCard>
					</BookingDashboardWrap>
				)}
				{user?.role == UserRole.User ? (
					<BookingTable
						listOrder={props.listOrder}
						reload={props.reload}
					/>
				) : user?.role == UserRole.Admin ? (
					<BookingTableAdmin
						listOrder={props.listOrder}
						reload={props.reload}
					/>
				) : (
					<BookingTablePartner
						listOrder={props.listOrder}
						reload={props.reload}
					/>
				)}
			</BookingContentWrap>
			<PaginationWrapper>
				<NumberPaginationBox
					page={page || 1}
					count={props.count}
					per={PERPAGE.PerPage}
					onChange={(val) => doChangePage?.(val)}
				/>
			</PaginationWrapper>
		</>
	);
}
